import React, { createContext, useContext, useMemo } from 'react';
import { useHistory, useLocation } from '@react-router';
import type { PageJson } from '@apps/card/routes/CardCustomizer/components/PageJson.types';
import { useColorways } from '@apps/card/hooks/useColorways';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';
import { enrichCardTelemetryExtraInfo, useCardTelemetry } from '@apps/card/Card.telemetry';
import { hasSuiteImage } from '@apps/card/utils/suiteImage';
import type { PdpColorwayControls, PdpColorwayControlsOrEmpty } from './ProductDetailsPage.types';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { convertProductTypeToCardFormat } from './utils';

const PdpColorwayContext = createContext<PdpColorwayControls | null>(null);

const EMPTY: PdpColorwayControlsOrEmpty = {};

type PdpColorwayProviderProps = Readonly<{
  /** Card front the colorways are read from. */
  page: PageJson | undefined;
  themeId: string;
  /** Null for digital-only themes, which have no printable template. */
  templateId?: string;
  children: React.ReactNode;
}>;

/**
 * Owns the PDP's colorway selection and shares it with every descendant that
 * needs it.
 *
 * Selection is a single source of truth across format switches, kept in sync
 * with the `colorway` query param (read on mount, written on select) so the
 * page is linkable and survives reload. Descendants read it with
 * `usePdpColorways` rather than receiving props, since the consumers — the
 * preview panes and the customization panes' `ColorwaySelector` — sit several
 * purely-forwarding layers below.
 */
export const PdpColorwayProvider = ({ page, themeId, templateId, children }: PdpColorwayProviderProps) => {
  const queryParams = useQueryParamHelper();
  const initialColorwayId = queryParams.getValueString('colorway') ?? undefined;

  const { options, selectedId, setSelectedId, cssVarStyle, preloadImages } = useColorways(page, undefined, initialColorwayId);

  // Enrich telemetry so every PDP event carries whether a suite image exists
  // for this design + colorway, enabling conversion analysis per-design.
  enrichCardTelemetryExtraInfo({ hasSuiteImage: hasSuiteImage(themeId, selectedId) });

  const history = useHistory();
  const location = useLocation();

  const { buttonInteracted } = useCardTelemetry();
  const { currentCategory } = useCardCategoryContext();
  const { activeProduct } = useStationeryProductPricingContext();

  const onColorwaySelect = useEventCallback((id: string) => {
    buttonInteracted({
      name: 'colorwaySwatchClicked',
      source: 'pdp',
      themeId,
      templateId,
      stationeryTemplateCategory: currentCategory,
      colorwayId: id,
      previousColorwayId: selectedId,
      swatchIndex: options?.findIndex(option => option.id === id) ?? -1,
      totalColorways: options?.length ?? 0,
      format: convertProductTypeToCardFormat(activeProduct.productType),
    });
    setSelectedId(id);
    const params = new URLSearchParams(location.search);
    params.set('colorway', id);
    history.replace({ search: params.toString() });
  });

  const value = useMemo<PdpColorwayControls>(
    () => ({ options, selectedColorwayId: selectedId, onColorwaySelect, cssVarStyle, preloadImages }),
    [options, selectedId, onColorwaySelect, cssVarStyle, preloadImages],
  );

  return <PdpColorwayContext.Provider value={value}>{children}</PdpColorwayContext.Provider>;
};

/**
 * Returns the PDP colorway selection. Outside a `PdpColorwayProvider` — the
 * basic-digital PDP carries no colorways — every field is absent, so callers
 * destructure directly and handle `undefined` rather than guarding on the
 * whole object.
 */
export const usePdpColorways = (): PdpColorwayControlsOrEmpty => useContext(PdpColorwayContext) ?? EMPTY;
